import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import httpClient, { tokenStorage } from '../services/httpClient';

interface SteeringState {
  isConnected: boolean;
  isRunning: boolean;
  currentMode: 'KALMAN' | 'PI';
  gnss_samples: number[];
  lock_status: string;
  n_gnss: number;
  ocxo_control: number;
  logs: string[];
  connectionMessage: string;
  controlValues: { p: number; i?: number };
  temperature: number;
  // Real-time data from MQTT
  realtime: {
    gnss_sample: number | null;
    ocxo_control: number | null;
    lastUpdated: string | null;
  };
}

interface SteeringContextType {
  state: SteeringState;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  startSteering: () => Promise<void>;
  stopSteering: () => Promise<void>;
  switchController: (type: 'KALMAN' | 'PI') => Promise<void>;
  clearLogs: () => void;
}

const SteeringContext = createContext<SteeringContextType | undefined>(undefined);

export const SteeringProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<SteeringState>({
    isConnected: false,
    isRunning: false,
    currentMode: 'KALMAN',
    gnss_samples: [],
    lock_status: 'unknown',
    n_gnss: 0,
    ocxo_control: 0,
    logs: [],
    connectionMessage: '',
    controlValues: { p: 0, i: 0 },
    temperature: 0,
    realtime: {
      gnss_sample: null,
      ocxo_control: null,
      lastUpdated: null,
    }
  });

  const socketRef = useRef<Socket | null>(null);
  const SOCKET_URL = (process.env.REACT_APP_SOCKET_URL as string) || window.location.origin;

  useEffect(() => {
    // Check initial connection status
    httpClient.get(`/status`)
      .then(response => {
        setState(prev => ({
          ...prev,
          isConnected: response.data.is_connected,
          isRunning: response.data.is_running
        }));
      })
      .catch(console.error);

    // Initialize Socket.IO connection with JWT auth
    socketRef.current = io(SOCKET_URL, {
      transports: ['websocket'],
      autoConnect: !!tokenStorage.getAccess(),
      path: '/socket.io',
      auth: (cb) => cb({ token: tokenStorage.getAccess() || '' }),
    });

    // Socket.IO event handlers
    socketRef.current.on('connect', () => {
      console.log('Socket.IO connected');
    });

    socketRef.current.on('disconnect', () => {
      console.log('Socket.IO disconnected');
    });

    socketRef.current.on('samples', (data: number[]) => {
      setState(prev => ({
        ...prev,
        gnss_samples: [...prev.gnss_samples.slice(-100), ...data],
      }));
    });

    socketRef.current.on('control_values', (data: { p: number; i?: number }) => {
      setState(prev => ({
        ...prev,
        controlValues: data
      }));
    });

    socketRef.current.on('temperature', (temp: number) => {
      setState(prev => ({
        ...prev,
        temperature: temp
      }));
    });

    socketRef.current.on('connection_status', (data: { status: string; message: string }) => {
      setState(prev => ({
        ...prev,
        isConnected: data.status === 'connected',
        connectionMessage: data.message,
        logs: [...prev.logs, `Connection: ${data.message}`]
      }));
    });

    socketRef.current.on('modo_status', (data: {
      gnss_samples: number[];
      lock_status: string;
      n_gnss: number;
      ocxo_control: number;
    }) => {
      setState(prev => ({
        ...prev,
        gnss_samples: data.gnss_samples,
        lock_status: data.lock_status,
        n_gnss: data.n_gnss,
        ocxo_control: data.ocxo_control
      }));
    });

    // Real-time GNSS sample updates from MQTT
    socketRef.current.on('gnss_sample', (data: {
      device_id: number;
      mqtt_id: string;
      value: number;
      timestamp: string;
    }) => {
      setState(prev => ({
        ...prev,
        realtime: {
          ...prev.realtime,
          gnss_sample: data.value,
          lastUpdated: data.timestamp,
        }
      }));
    });

    // Real-time OCXO control updates from MQTT
    socketRef.current.on('ocxo_control', (data: {
      device_id: number;
      mqtt_id: string;
      value: number;
      timestamp: string;
    }) => {
      setState(prev => ({
        ...prev,
        realtime: {
          ...prev.realtime,
          ocxo_control: data.value,
          lastUpdated: data.timestamp,
        }
      }));
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const connect = async () => {
    try {
      const response = await httpClient.post(`/connect`);
      if (response.data.status === 'connected') {
        setState(prev => ({
          ...prev,
          isConnected: true,
          connectionMessage: response.data.message,
          logs: [...prev.logs, `Connection: ${response.data.message}`]
        }));
      }
    } catch (error) {
      console.error('Failed to connect:', error);
      setState(prev => ({
        ...prev,
        logs: [...prev.logs, `Connection failed: ${error}`]
      }));
    }
  };

  const disconnect = async () => {
    try {
      const response = await httpClient.post(`/disconnect`);
      setState(prev => ({
        ...prev,
        isConnected: false,
        isRunning: false,
        connectionMessage: response.data.message,
        logs: [...prev.logs, `Connection: ${response.data.message}`]
      }));
    } catch (error) {
      console.error('Failed to disconnect:', error);
    }
  };

  const startSteering = async () => {
    try {
      await httpClient.post(`/start`);
      setState(prev => ({ ...prev, isRunning: true }));
    } catch (error) {
      console.error('Failed to start steering:', error);
    }
  };

  const stopSteering = async () => {
    try {
      await httpClient.post(`/stop`);
      setState(prev => ({ ...prev, isRunning: false }));
    } catch (error) {
      console.error('Failed to stop steering:', error);
    }
  };

  const switchController = async (type: 'KALMAN' | 'PI') => {
    try {
      await httpClient.post(`/switch_controller`, { type });
      setState(prev => ({ ...prev, currentMode: type }));
    } catch (error) {
      console.error('Failed to switch controller:', error);
    }
  };

  const clearLogs = () => {
    setState(prev => ({ ...prev, logs: [] }));
  };

  return (
    <SteeringContext.Provider
      value={{
        state,
        connect,
        disconnect,
        startSteering,
        stopSteering,
        switchController,
        clearLogs,
      }}
    >
      {children}
    </SteeringContext.Provider>
  );
};

export const useSteeringContext = () => {
  const context = useContext(SteeringContext);
  if (context === undefined) {
    throw new Error('useSteeringContext must be used within a SteeringProvider');
  }
  return context;
}; 